import { Component, OnInit } from '@angular/core';
import {
  AuditLog,
  AuditLogResponse,
  AuditRow,
} from '../../../models/Audit/audit';
import { AuditService } from '../../../services/audit.service';
import { DatePipe } from '@angular/common';
import { CommonService } from '../../../services/common/common.service';
import { CompanyService } from '../../../services/company/company.service';
import { AuthService } from '../../../services/auth.service';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx-js-style';
import moment from 'moment';

interface UserSession {
  username: string;
  employeeCode: string;
  employeeName: string;
  loginTime: Date | null;
  logoutTime: Date | null;
  loginTimeFormatted: string;
  logoutTimeFormatted: string;
  ipAddress: string;
}

@Component({
  selector: 'app-system-user-log',
  templateUrl: './system-user-log.component.html',
  styleUrl: './system-user-log.component.scss',
})
export class SystemUserLogComponent implements OnInit {
  sessions: UserSession[] = [];
  filters: any = {
    fromDate: '',
    toDate: '',
    searchText: '',
  };
  page: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  dateFormat: any = 'dd/MM/yy';
  date: any = 'dd-mm-yy';
  angularDateFormat: string = '';
  isMenuOpen: boolean = false;
  companyName: string = '';

  // Custom table
  menuId: number = 0;
  myColumns: any[] = [];
  filterPayload: any = '';
  orderby: string = '';
  order: string = '';
  searchQuery: string = '';
  private searchDebounceTimer: any = null;

  constructor(
    private readonly auditService: AuditService,
    private datePipe: DatePipe,
    private commonService: CommonService,
    private companyService: CompanyService,
    private readonly auth: AuthService,
  ) { }

  ngOnInit(): void {
    this.menuId = this.auth.selectedMenuID ?? 0;
    const data: any = this.commonService.getLocalStorageData();
    if (data && !data.client) {
      this.getDateFormat(data.companyID);
      this.loadCompanyName(data.companyID);
    } else if (data?.client && data?.firstname) {
      this.companyName = String(data.firstname);
    }
    this.loadSessions();
  }

  private loadCompanyName(companyId: any): void {
    this.companyService.getCompanybyID(companyId).subscribe({
      next: (resp: any) => {
        this.companyName = String(resp?.data?.company_name || '').trim();
      },
      error: () => { /* no-op */ },
    });
  }

  getDateFormat(companyId: any): void {
    this.companyService.getParametersById(companyId).subscribe({
      next: (resp) => {
        if (resp?.data?.date_format) {
          this.date = resp?.data?.date_format;
          this.dateFormat = resp?.data?.date_format.replace('mm', 'MM');
          this.angularDateFormat =
            this.commonService.convertPrimeNGToAngularFormat(this.date);
          if (this.sessions.length) {
            this.sessions = this.sessions.map((s) => ({
              ...s,
              loginTimeFormatted: this.formatDateTime(s.loginTime),
              logoutTimeFormatted: this.formatDateTime(s.logoutTime),
            }));
          }
        }
      },
      error: (err) => console.log(err),
    });
  }

  private formatDateTime(value: Date | null): string {
    if (!value) return '';
    const fmt = (this.angularDateFormat || this.dateFormat) + ' hh:mm a';
    return this.datePipe.transform(value, fmt) || '';
  }

  private buildDateParams(): { fromDate?: string; toDate?: string } {
    const p: any = {};
    if (this.filters.fromDate) {
      p.fromDate =
        this.datePipe.transform(this.filters.fromDate, 'yyyy-MM-dd') || '';
    }
    if (this.filters.toDate) {
      p.toDate =
        this.datePipe.transform(this.filters.toDate, 'yyyy-MM-dd') || '';
    }
    return p;
  }

  loadSessions(): void {
    const dateParams = this.buildDateParams();

    const signinParams: any = {
      ...dateParams,
      actions: 'SIGNIN',
      searchText: this.searchQuery || this.filters.searchText || '',
      page: this.page - 1,
      size: this.pageSize,
      ...this.commonService.buildFilterPayload(this.filterPayload),
    };
    if (this.orderby) signinParams['orderby'] = this.orderby;
    if (this.order) signinParams['order'] = this.order;

    const signoutParams: any = {
      ...dateParams,
      actions: 'SIGNOUT',
      searchText: this.filters.searchText || '',
      page: 0,
      size: 10000,
    };

    forkJoin({
      signins: this.auditService.getSystemUserLogs(signinParams),
      signouts: this.auditService.getSystemUserLogs(signoutParams),
    }).subscribe({
      next: ({ signins, signouts }) => {
        const signinRows: AuditRow[] = signins?.data?.data?.rows || [];
        const signoutRows: AuditRow[] = signouts?.data?.data?.rows || [];
        this.totalRecords = signins?.data?.totalItems || 0;
        let sessions = this.pairSessions(signinRows, signoutRows);
        // Logout time is determined after pairing (from SIGNOUT records), so
        // backend cannot sort by it. Apply client-side sort when needed.
        if (this.orderby === 'logoutTimeFormatted') {
          const dir = this.order === 'asc' ? 1 : -1;
          sessions = sessions.sort((a, b) => {
            const aMs = a.logoutTime?.getTime() ?? null;
            const bMs = b.logoutTime?.getTime() ?? null;
            if (aMs === null && bMs === null) return 0;
            // ASC: nulls to bottom; DESC: nulls to top
            if (aMs === null) return dir > 0 ? 1 : -1;
            if (bMs === null) return dir > 0 ? -1 : 1;
            return (aMs - bMs) * dir;
          });
        }
        this.sessions = sessions;
        if (this.myColumns.length === 0) this.buildColumns();
      },
      error: (err) => console.error(err),
    });
  }

  private parseUsername(username: string): { name: string; code: string } {
    const match = username?.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (match) return { name: match[1].trim(), code: match[2].trim() };
    return { name: username || '', code: '' };
  }

  private parseAmendmentDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d;
    // Handle "20-May-2026 09:00" → "20 May 2026 09:00"
    const d2 = new Date(dateStr.replace(/-/g, ' '));
    return isNaN(d2.getTime()) ? null : d2;
  }

  private getRowTime(row: AuditRow, isoField: string): Date | null {
    const iso = (row as any)[isoField];
    if (iso) return new Date(iso);
    return this.parseAmendmentDate(row['Amendment On']);
  }

  private pairSessions(
    signins: AuditRow[],
    signouts: AuditRow[],
  ): UserSession[] {
    return signins.map((signin) => {
      const amendmentBy = signin['Amendment By'] || '';
      const { name, code } = this.parseUsername(amendmentBy);
      const loginTime = this.getRowTime(signin, 'loginTimestamp');

      const matchingSignout = signouts
        .filter((so) => {
          const signoutTime = this.getRowTime(so, 'logoutTimestamp');
          return (
            so['Amendment By'] === amendmentBy &&
            loginTime &&
            signoutTime &&
            signoutTime.getTime() > loginTime.getTime()
          );
        })
        .sort(
          (a, b) =>
            (this.getRowTime(a, 'logoutTimestamp')?.getTime() ?? 0) -
            (this.getRowTime(b, 'logoutTimestamp')?.getTime() ?? 0),
        )[0];

      const logoutTime = matchingSignout
        ? this.getRowTime(matchingSignout, 'logoutTimestamp')
        : null;

      return {
        username: amendmentBy,
        employeeCode: code,
        employeeName: code ? name : '',
        loginTime,
        logoutTime,
        loginTimeFormatted: this.formatDateTime(loginTime),
        logoutTimeFormatted: this.formatDateTime(logoutTime),
        ipAddress: ((signin as any)['IP Address'] || '').split(',')[0].trim(),
      };
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadSessions();
  }

  handlePageChange(newPage: number): void {
    this.page = newPage;
    this.loadSessions();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  exportToExcel(): void {
    this.isMenuOpen = false;
    const dateParams = this.buildDateParams();

    const signinParams: any = {
      ...dateParams,
      actions: 'SIGNIN',
      searchText: this.filters.searchText || '',
      page: 0,
      size: this.totalRecords || 10000,
    };

    const signoutParams: any = {
      ...dateParams,
      actions: 'SIGNOUT',
      searchText: this.filters.searchText || '',
      page: 0,
      size: 10000,
    };

    forkJoin({
      signins: this.auditService.getSystemUserLogs(signinParams),
      signouts: this.auditService.getSystemUserLogs(signoutParams),
    }).subscribe({
      next: ({ signins, signouts }) => {
        const signinRows: AuditRow[] = signins?.data?.data?.rows || [];
        const signoutRows: AuditRow[] = signouts?.data?.data?.rows || [];
        const allSessions = this.pairSessions(signinRows, signoutRows);
        if (!allSessions.length) return;

        const fmt = (this.angularDateFormat || this.dateFormat) + ' hh:mm a';
        const rows = allSessions.map((s) => ({
          Username: s.username,
          'Employee Code': s.employeeCode,
          'Employee Name': s.employeeName,
          'Login Time': s.loginTime
            ? this.datePipe.transform(s.loginTime, fmt) || ''
            : '',
          'Logout Time': s.logoutTime
            ? this.datePipe.transform(s.logoutTime, fmt) || ''
            : '',
          'IP Address': s.ipAddress,
        }));

        const headers = [
          'Username',
          'Employee Code',
          'Employee Name',
          'Login Time',
          'Logout Time',
          'IP Address',
        ];
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([[], [], [], []]);

        const downloadDate = moment().format(
          this.commonService.convertPrimeNGToMomentFormat(this.dateFormat)
        );
        XLSX.utils.sheet_add_aoa(
          ws,
          [
            [`Downloaded Date: ${downloadDate}`],
            [this.companyName || ''],
            ['System User Log List'],
          ],
          { origin: 'A1' },
        );
        XLSX.utils.sheet_add_json(ws, rows, { header: headers, origin: 'A4' });

        const titleStyle: any = {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: 'center' },
        };
        const downloadDateStyle: any = {
          font: { bold: true, sz: 11 },
          alignment: { horizontal: 'right' },
        };
        const dlCell = ws[XLSX.utils.encode_cell({ r: 0, c: 0 })];
        if (dlCell) dlCell.s = downloadDateStyle;
        const cnCell = ws[XLSX.utils.encode_cell({ r: 1, c: 0 })];
        if (cnCell) cnCell.s = titleStyle;
        const rnCell = ws[XLSX.utils.encode_cell({ r: 2, c: 0 })];
        if (rnCell) rnCell.s = titleStyle;

        ws['!merges'] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
          { s: { r: 2, c: 0 }, e: { r: 2, c: headers.length - 1 } },
        ];

        const headerStyle = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4472C4' } },
          alignment: { horizontal: 'center' },
        };
        headers.forEach((_, i) => {
          const cell = ws[XLSX.utils.encode_cell({ r: 3, c: i })];
          if (cell) cell.s = headerStyle;
        });

        ws['!cols'] = headers.map((h) => ({ wch: Math.max(h.length + 2, 18) }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'System User Log List');
        XLSX.writeFile(
          wb,
          `System_User_Log_List_${this.datePipe.transform(new Date(), 'yyyyMMdd_HHmm')}.xlsx`,
        );
      },
      error: (err) => console.error(err),
    });
  }

  buildColumns(): void {
    this.myColumns = [
      { field: 'username', header: 'Username' },
      { field: 'employeeCode', header: 'Employee Code' },
      { field: 'employeeName', header: 'Employee Name' },
      { field: 'loginTimeFormatted', header: 'Login Time', isDate: true },
      { field: 'logoutTimeFormatted', header: 'Logout Time', isDate: true },
      { field: 'ipAddress', header: 'IP Address' },
    ];
  }

  handleFilterChange(payload: any): void {
    this.filterPayload = payload;
    if (payload) {
      if (payload.orderby !== undefined) this.orderby = payload.orderby;
      if (payload.order !== undefined) this.order = payload.order;
      if (payload.page !== undefined) this.page = payload.page + 1;
      if (payload.pageSize !== undefined) this.pageSize = payload.pageSize;
    }
    this.loadSessions();
  }

  onSearchChange(): void {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.page = 1;
      this.loadSessions();
    }, 400);
  }
}
